import { useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import { useResolvedPath } from 'react-router-dom';
import {
  BreadcrumbEntry,
  useBreadcrumbEntries,
  type PageLayoutProps,
  type PageLayoutTab,
} from '@backstage/frontend-plugin-api';
import { PluginHeader, Tab, TabList, Tabs } from '@backstage/ui';

/** On BUI's PluginHeader when the tabs are ours, under it. */
export const TABBED_PLUGIN_HEADER_CLASS = 'waadoo-plugin-header--tabbed';
/** The row of the page's tabs (shared folder-tab rules, styles.css). */
export const PLUGIN_TABS_CLASS = 'waadoo-plugin-tabs';

/**
 * The header block of a page made of sub-pages: BUI's PluginHeader without
 * tabs, then ours, with the sub-pages' icons; the sub-page under it.
 *
 * The content's `--bui-header-height` is the whole block's: BUI's
 * PluginHeader writes its own height on the document root (the variable
 * BUI's FullPage takes off the viewport's), which no longer counts the
 * tabs; set on the content's wrapper instead, the nearer value wins for
 * everything inside it — the app visualizer's FullPage among them. Measured
 * as PluginHeader measures itself (`offsetHeight` and margin-bottom),
 * kept up by a ResizeObserver where there is one.
 */
function TabbedPage(props: {
  header: ReactNode;
  tabs: PageLayoutTab[];
  /** The tab list's accessible name: the page's title. */
  label: string | undefined;
  children: ReactNode;
}) {
  const { header, tabs, label, children } = props;
  const headRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const head = headRef.current;
    const content = contentRef.current;
    if (!head || !content) {
      return undefined;
    }
    const measure = () => {
      const margin = parseFloat(window.getComputedStyle(head).marginBottom);
      content.style.setProperty(
        '--bui-header-height',
        `${head.offsetHeight + (Number.isNaN(margin) ? 0 : margin)}px`,
      );
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(head);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div ref={headRef} className="waadoo-plugin-head">
        {header}
        <div className={PLUGIN_TABS_CLASS}>
          <Tabs>
            <TabList aria-label={label}>
              {tabs.map(tab => (
                <Tab
                  key={tab.id}
                  id={tab.id}
                  href={tab.href}
                  matchStrategy="prefix"
                >
                  {tab.icon ? (
                    <span className="waadoo-icon-label">
                      <span
                        className="waadoo-plugin-tabs__icon"
                        aria-hidden="true"
                      >
                        {tab.icon}
                      </span>
                      {tab.label}
                    </span>
                  ) : (
                    tab.label
                  )}
                </Tab>
              ))}
            </TabList>
          </Tabs>
        </div>
      </div>
      <div ref={contentRef}>{children}</div>
    </>
  );
}

/**
 * The app's page layout (`PageLayout`, the swappable component every
 * PageBlueprint page renders), in place of @backstage/plugin-app's: the same
 * header — BUI's PluginHeader with the page's title, icon, breadcrumbs and
 * actions — and the same breadcrumb entry, except for the tabs of a page
 * made of sub-pages, which are the app's folder tabs with the sub-pages'
 * icons (`PageLayoutTab.icon`): @backstage/ui 0.18's PluginHeader types a
 * tab's label as a string and renders no icon.
 *
 * Mirrors plugin-app 0.5's `core-page-layout` (extensions/components.esm.js):
 * relative tab hrefs resolved against the page's path, active by prefix.
 */
export function WaadooPageLayout(props: PageLayoutProps) {
  const { title, icon, noHeader, titleLink, headerActions, tabs, children } =
    props;
  const parentPath = useResolvedPath('.').pathname.replace(/\/$/, '');
  const resolvedTabs = useMemo(
    () =>
      tabs?.map(tab => ({
        ...tab,
        href: tab.href.startsWith('/')
          ? tab.href
          : `${parentPath}/${tab.href}`.replace(/\/{2,}/g, '/'),
      })),
    [tabs, parentPath],
  );
  const { items: breadcrumbs } = useBreadcrumbEntries();

  if (noHeader) {
    return <>{children}</>;
  }

  const headerProps = {
    title,
    icon,
    titleLink,
    breadcrumbs,
    customActions: headerActions,
  };
  const content =
    resolvedTabs && resolvedTabs.length > 0 ? (
      <TabbedPage
        header={
          <PluginHeader
            {...headerProps}
            className={TABBED_PLUGIN_HEADER_CLASS}
          />
        }
        tabs={resolvedTabs}
        label={title}
      >
        {children}
      </TabbedPage>
    ) : (
      <>
        <PluginHeader {...headerProps} />
        {children}
      </>
    );

  if (!title) {
    return content;
  }
  return (
    <BreadcrumbEntry
      entry={{ label: title, href: titleLink ?? (parentPath || '/') }}
    >
      {content}
    </BreadcrumbEntry>
  );
}
